import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "zodios/client";
import { css } from "../../../styled-system/css";
import { Flex } from "../../../styled-system/jsx";
import { Button } from "components/core/basic/Button";
import { TextField } from "components/fields/base/TextField";
import { successToast } from "utils/toasts";
import { HoverInfo } from "components/core/basic/Tooltip";
import { AuthLayout } from "layouts/AuthLayout";

interface UpdatePasswordFormData {
  token: string;
  newPassword: string;
}

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const history = useHistory();

  const setNewPasswordMutation = useMutation({
    mutationFn: async (updatePasswordData: UpdatePasswordFormData) => {
      await apiClient.processPasswordReset(updatePasswordData);
    },
    onError: (error) => {
      if (typeof error.message === "string" && !error.message.includes("400")) {
        setError(error.message);
      } else {
        setError(
          "Something went wrong while setting your new password.\n\nPlease try again or contact customer success.",
        );
      }
    },
    onSuccess: () => {
      successToast({ message: "Password successfully updated." });
      history.push("/");
    },
  });

  return (
    <AuthLayout title={"Reset your password"} subtitle={"Enter your password below."}>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          if (password !== confirmPassword) {
            setError("Password and password confirmation do not match.");
            return;
          }

          const queryParams = new URLSearchParams(window.location.search);
          const token = queryParams.get("user_token");

          if (!token) {
            setError("A token is required");
            history.push("/");
            return;
          }

          setNewPasswordMutation.mutate({
            token,
            newPassword: password,
          });
        }}
      >
        <Flex gap={"medium"} direction={"column"}>
          <TextField
            label={
              <span className={css({ display: "flex", gap: "small" })}>
                Password{" "}
                <HoverInfo iconClassName={css({ color: "grey", fontSize: "small" })}>
                  Password must be at least 8 characters long and include at least one uppercase letter, one lowercase
                  letter, and one number.
                </HoverInfo>
              </span>
            }
            type={"password"}
            onChange={(value) => {
              setPassword(value);
            }}
          />
          <TextField
            label={"Confirm Password"}
            type={"password"}
            onChange={(value) => {
              setConfirmPassword(value);
            }}
          />
        </Flex>
        <Button
          isPending={setNewPasswordMutation.isPending}
          isDisabled={!password.trim().length || !confirmPassword.trim().length}
          className={css({ width: "100%", marginTop: "large" })}
          type="submit"
        >
          Reset Password
        </Button>
      </form>
      <div className={css({ width: "100%", marginTop: "medium", textAlign: "center" })}>
        <Link to="/">Sign In Instead</Link>
      </div>
      {error && <p className={css({ paddingY: "large", textAlign: "center" })}>{error}</p>}
    </AuthLayout>
  );
};

export default ResetPassword;
